import { chunk } from 'lodash';
/**
 * Executes a given function in chunks on a list, introducing a delay between each chunk iteration to maintain UI responsiveness.
 * @template T - The type of elements in the list.
 * @template R - The type of elements returned by the function.
 * @param {number} chunkSize - The size of each chunk.
 * @param {T[]} list - The list to be chunked and processed.
 * @param {(chunk: T[]) => R[]} func - The function to be executed on each chunk.
 * @returns {Promise<R[]>} - A Promise that resolves to an array containing the results of executing the function on each chunk.
 */
export async function executeFunctionInChunks<T, R>(
  chunkSize: number,
  list: T[],
  func: (chunk: T[]) => R[],
  pauseIntervalMs: number = 0,
): Promise<R[]> {
  let results: R[] = [];

  // Pauses execution for a short duration to maintain UI responsiveness.
  const pause = async (): Promise<void> => new Promise(r => setTimeout(r, pauseIntervalMs));

  for (const group of chunk(list, chunkSize)) {
    results = [...results, ...func(group)];
    await pause();
  }

  return results;
}
